import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CalendarClock, LayoutGrid, List, MapPin, Search, Wrench } from 'lucide-react';
import api from '../../lib/api';
import { toast } from '../../hooks/use-toast';
import { SoldReviewCard } from './SoldReviewCard';

export const SR_STATUS = {
  waiting: { label: 'Aguardando W.O.', cls: 'text-sky-400 bg-sky-400/10', bar: '#38bdf8' },
  planned: { label: 'Programado', cls: 'text-cyan-300 bg-cyan-300/10', bar: '#67e8f9' },
  in_shop: { label: 'Na Oficina', cls: 'text-amber-400 bg-amber-400/10', bar: '#fbbf24' },
  waiting_parts: { label: 'Aguardando Peça', cls: 'text-orange-400 bg-orange-400/10', bar: '#fb923c' },
  done: { label: 'Serviço Finalizado', cls: 'text-emerald-400 bg-emerald-400/10', bar: '#34d399' },
};
export const PRIO_CLS = { Alta: 'text-red-400 border-red-400/40', Média: 'text-amber-400 border-amber-400/40', Baixa: 'text-muted-foreground border-border' };
export const fmtD = (s) => (s ? s.slice(0, 10).split('-').reverse().slice(0, 2).join('/') : '—');

export const DueTag = ({ c }) => {
  const sr = c.sold_review || {};
  if (sr.status === 'done') return <span className="ent-tag t-ok">Finalizado {fmtD(sr.finished_at)}</span>;
  if (!sr.promised_date) return <span className="ent-tag">Sem data prometida</span>;
  if (c.overdue) return <span className="ent-tag t-bad border-red-400/40" data-testid={`sr-overdue-${c.id}`}><AlertTriangle className="w-3 h-3" /> Atrasado {Math.abs(c.days_left)}d</span>;
  if (c.days_left <= 2) return <span className="ent-tag t-warn border-amber-400/40">Entrega {fmtD(sr.promised_date)} · {c.days_left === 0 ? 'hoje' : `${c.days_left}d`}</span>;
  return <span className="ent-tag">Entrega {fmtD(sr.promised_date)}</span>;
};

const Card = ({ c, onOpen, draggable }) => {
  const sr = c.sold_review;
  return (
    <button data-testid={`sr-card-${c.id}`} draggable={draggable} onDragStart={(e) => e.dataTransfer.setData('srDealId', c.id)} onClick={() => onOpen(c.id)}
      className={`w-full text-left rounded-lg border bg-popover p-3 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/50 ${c.overdue ? 'border-red-400/40' : 'border-border'} ${draggable ? 'cursor-grab active:cursor-grabbing' : ''}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold text-[13px] truncate">{c.customer_name}</p>
        <span className={`ent-tag ${PRIO_CLS[sr.priority]}`}>{sr.priority}</span>
      </div>
      <p className="text-[12px] text-muted-foreground mt-0.5 truncate">{c.vehicle_desc} · #{c.stock}</p>
      <p className="mono text-[10px] t-label truncate" data-testid={`sr-card-vin-${c.id}`}>VIN {c.vin || '—'}</p>
      <p className="text-[10.5px] t-label mt-0.5 truncate">{c.salesperson_name} · vendido há {c.days_since_sale}d</p>
      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
        <DueTag c={c} />
        {sr.location && sr.location !== 'Pátio' && <span className="ent-tag"><MapPin className="w-3 h-3" /> {sr.location}</span>}
      </div>
      {c.items_total > 0 && (
        <div className="mt-2 flex items-center gap-2">
          <div className="h-1.5 flex-1 rounded-full bg-muted/50 overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${(c.items_done / c.items_total) * 100}%`, background: c.all_done ? '#34d399' : 'hsl(var(--primary))' }} /></div>
          <span className="text-[10px] font-bold text-muted-foreground tabular-nums">{c.items_done}/{c.items_total}</span>
        </div>
      )}
      {c.all_done && sr.status !== 'done' && <p className="mt-1.5 text-[10.5px] font-bold uppercase t-ok">✓ W.O. concluído — pronto p/ finalizar</p>}
    </button>
  );
};

const Column = ({ k, cards, onOpen, onDrop, canEdit }) => {
  const [over, setOver] = useState(false);
  const s = SR_STATUS[k];
  return (
    <div data-testid={`sr-col-${k}`} className={`flex-1 min-w-[250px] rounded-xl border bg-muted/15 p-2.5 transition-all ${over ? 'kanban-active' : 'border-border/60'}`}
      onDragOver={(e) => { if (canEdit && k !== 'done') { e.preventDefault(); setOver(true); } }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); const id = e.dataTransfer.getData('srDealId'); if (id && k !== 'done') onDrop(id, k); }}>
      <div className="flex items-center justify-between mb-2.5 px-1" style={{ borderTop: `2px solid ${s.bar}`, paddingTop: 8 }}>
        <span className={`text-[11px] font-bold px-2 py-1 rounded-full ${s.cls}`}>{s.label}</span>
        <span className="text-xs font-semibold text-primary bg-primary/12 px-2 py-0.5 rounded-full">{cards.length}</span>
      </div>
      <div className="space-y-2">
        {cards.map((c) => <Card key={c.id} c={c} onOpen={onOpen} draggable={canEdit && k !== 'done'} />)}
        {cards.length === 0 && <p className="text-[12px] text-muted-foreground/50 text-center py-6">Vazio</p>}
      </div>
    </div>
  );
};

const SORTS = [['auto', 'Prioridade + data'], ['promised', 'Data prometida'], ['age', 'Dias desde a venda'], ['seller', 'Vendedor']];

export const SoldReview = ({ canEdit }) => {
  const [data, setData] = useState({ kpis: {}, cards: [] });
  const [view, setView] = useState('kanban');
  const [q, setQ] = useState('');
  const [prio, setPrio] = useState('');
  const [onlyLate, setOnlyLate] = useState(false);
  const [sort, setSort] = useState('auto');
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try { const { data: r } = await api.get('/service/sold-review'); setData(r); } catch (e) {}
  }, []);
  useEffect(() => { load(); const iv = setInterval(load, 20000); return () => clearInterval(iv); }, [load]);

  const cards = useMemo(() => {
    let rows = data.cards;
    const s = q.trim().toLowerCase();
    if (s) rows = rows.filter((c) => `${c.customer_name} ${c.vehicle_desc} ${c.stock} ${c.vin || ''} ${c.salesperson_name} ${c.id} ${(c.sold_review.items || []).map((i) => i.servico).join(' ')}`.toLowerCase().includes(s));
    if (prio) rows = rows.filter((c) => c.sold_review.priority === prio);
    if (onlyLate) rows = rows.filter((c) => c.overdue);
    if (sort === 'promised') rows = [...rows].sort((a, b) => (a.sold_review.promised_date || '9') .localeCompare(b.sold_review.promised_date || '9'));
    if (sort === 'age') rows = [...rows].sort((a, b) => b.days_since_sale - a.days_since_sale);
    if (sort === 'seller') rows = [...rows].sort((a, b) => a.salesperson_name.localeCompare(b.salesperson_name));
    return rows;
  }, [data.cards, q, prio, onlyLate, sort]);

  const drop = async (id, status) => {
    const c = data.cards.find((x) => x.id === id);
    if (!c || c.sold_review.status === status || c.sold_review.status === 'done') return;
    try { await api.put(`/service/sold-review/${id}`, { status }); load(); }
    catch (e) { toast({ title: 'Não foi possível mover', description: e.response?.data?.detail || '' }); }
  };

  const k = data.kpis;
  return (
    <div className="space-y-3" data-testid="sold-review">
      <div className="ent-ribbon" data-testid="sr-kpis">
        <div><p className="ent-label">Vendidos sem W.O.</p><p className="ent-value mt-1 text-sky-400" data-testid="sr-kpi-waiting">{k.waiting ?? 0}</p></div>
        <div><p className="ent-label">Programados</p><p className="ent-value mt-1">{k.planned ?? 0}</p></div>
        <div><p className="ent-label">Na oficina</p><p className="ent-value mt-1 t-warn">{k.in_shop ?? 0}</p></div>
        <div><p className="ent-label">Aguardando peça</p><p className="ent-value mt-1 text-orange-400">{k.waiting_parts ?? 0}</p></div>
        <div><p className="ent-label">Atrasados</p><p className="ent-value mt-1 t-bad" data-testid="sr-kpi-overdue">{k.overdue ?? 0}</p></div>
        <div><p className="ent-label">Entrega em ≤ 2 dias</p><p className="ent-value mt-1 t-warn">{k.due_soon ?? 0}</p></div>
        <div><p className="ent-label">Prontos p/ finalizar</p><p className="ent-value mt-1 t-ok">{k.ready_to_finish ?? 0}</p></div>
        <div><p className="ent-label">Finalizados hoje</p><p className="ent-value mt-1">{k.finished_today ?? 0}</p></div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex rounded-md border border-border overflow-hidden">
          <button data-testid="sr-view-kanban" onClick={() => setView('kanban')} className={`px-2.5 h-[30px] text-[12px] font-semibold flex items-center gap-1.5 ${view === 'kanban' ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'}`}><LayoutGrid className="w-3.5 h-3.5" /> Kanban</button>
          <button data-testid="sr-view-list" onClick={() => setView('list')} className={`px-2.5 h-[30px] text-[12px] font-semibold flex items-center gap-1.5 ${view === 'list' ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'}`}><List className="w-3.5 h-3.5" /> Lista</button>
        </div>
        {['', 'Alta', 'Média', 'Baixa'].map((p) => (
          <button key={p || 'all'} data-testid={`sr-prio-${p || 'all'}`} onClick={() => setPrio(p)} className={`ent-btn ent-btn-sm ${prio === p ? 'ent-btn-primary' : ''}`}>{p || 'Todas prioridades'}</button>
        ))}
        <button data-testid="sr-only-late" onClick={() => setOnlyLate(!onlyLate)} className={`ent-btn ent-btn-sm ${onlyLate ? 'ent-btn-primary' : 't-bad'}`}><AlertTriangle className="w-3 h-3" /> Só atrasados</button>
        <select data-testid="sr-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="ent-input" style={{ width: 190, height: 30 }}>
          {SORTS.map(([v, l]) => <option key={v} value={v} className="bg-popover text-foreground">Ordenar: {l}</option>)}
        </select>
        <div className="relative ml-auto">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/60 pointer-events-none" />
          <input data-testid="sr-search" className="ent-input pl-8" style={{ width: 230, height: 30 }} placeholder="Cliente, veículo, stock, serviço..." value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      {view === 'kanban' ? (
        <div className="flex gap-3 overflow-x-auto pb-2">
          {Object.keys(SR_STATUS).map((st) => <Column key={st} k={st} cards={cards.filter((c) => c.sold_review.status === st)} onOpen={setOpenId} onDrop={drop} canEdit={canEdit} />)}
        </div>
      ) : (
        <div className="ent-panel overflow-x-auto" data-testid="sr-list">
          <table className="ent-table">
            <thead><tr><th>Prior.</th><th>Cliente</th><th>Veículo</th><th>Vendedor</th><th>Etapa</th><th>Onde está</th><th>W.O.</th><th>Prometido</th><th>Previsão</th><th>Venda</th></tr></thead>
            <tbody>
              {cards.map((c) => {
                const sr = c.sold_review;
                return (
                  <tr key={c.id} data-testid={`sr-row-${c.id}`} onClick={() => setOpenId(c.id)} className="cursor-pointer">
                    <td><span className={`ent-tag ${PRIO_CLS[sr.priority]}`}>{sr.priority}</span></td>
                    <td className="font-medium">{c.customer_name}</td>
                    <td className="text-foreground/75">{c.vehicle_desc} <span className="mono t-label">#{c.stock}</span><span className="block mono text-[10px] t-label">VIN {c.vin || '—'}</span></td>
                    <td className="text-foreground/75">{c.salesperson_name}</td>
                    <td><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${SR_STATUS[sr.status].cls}`}>{SR_STATUS[sr.status].label}</span></td>
                    <td className="text-foreground/75">{sr.location}</td>
                    <td className="tabular-nums">{c.items_done}/{c.items_total}{(sr.items || []).length > 0 && <span className="t-label text-[11px]"> · {sr.items.map((i) => i.servico).slice(0, 2).join(', ')}{sr.items.length > 2 ? '…' : ''}</span>}</td>
                    <td><DueTag c={c} /></td>
                    <td className="t-label">{sr.eta_date ? <span className="inline-flex items-center gap-1"><CalendarClock className="w-3 h-3" />{fmtD(sr.eta_date)}</span> : '—'}</td>
                    <td className="t-label">{fmtD(c.sale_date)} · {c.days_since_sale}d</td>
                  </tr>
                );
              })}
              {cards.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-[12px] text-muted-foreground/60"><Wrench className="w-4 h-4 inline mr-1" /> Nenhum veículo vendido em revisão</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <SoldReviewCard dealId={openId} open={!!openId} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
};

export default SoldReview;
